import { SalesComparisonTablePages } from '../components/SalesComparisonTablePages.tsx';
import { getSalesComparisonRows } from '../constants.ts';
import { SalesComparisonGridColumnData, SalesComparisonPageProps } from '../types.ts';
import { getSalesComparisonNestedValue } from '../utils/getSalesComparisonNestedValue.ts';
import { shouldIncludeSalesComparisonRow } from '../utils/shouldIncludeSalesComparisonRow.ts';

export const SalesComparisonPage = ({
  subjectAddress,
  subjectSalesComparisonData,
  subjectArvSalesComparisonData,
  comparables,
  hasArvOption,
  includeTotalCostToCure,
  includeCostApproach,
  salesComparisonApproach,
  orderFeatures,
  opinionOfMarketValue,
  opinionOfAfterRepairValue,
}: SalesComparisonPageProps) => {
  const rows = getSalesComparisonRows(salesComparisonApproach);
  const compsPerPage = 3;
  const pages: SalesComparisonGridColumnData[][] = [];
  const aivPages: SalesComparisonGridColumnData[][] = [];
  const arvPages: SalesComparisonGridColumnData[][] = [];
  const noTypeComps = comparables.filter((comp) => !comp.compType);
  const aivComps = comparables.filter((comp) => comp.compType === 'aiv');
  const arvComps = comparables.filter((comp) => comp.compType === 'arv');
  for (let i = 0; i < noTypeComps.length; i += compsPerPage) {
    pages.push(noTypeComps.slice(i, i + compsPerPage));
  }

  for (let i = 0; i < aivComps.length; i += compsPerPage) {
    aivPages.push(aivComps.slice(i, i + compsPerPage));
  }

  for (let i = 0; i < arvComps.length; i += compsPerPage) {
    arvPages.push(arvComps.slice(i, i + compsPerPage));
  }

  const properties = !hasArvOption
    ? [subjectSalesComparisonData, ...comparables]
    : [subjectSalesComparisonData, subjectArvSalesComparisonData, ...comparables];

  const filteredRows = rows.filter((field) => {
    const cellValues = field.key.flatMap((fieldPath) =>
      properties.map((comp) => getSalesComparisonNestedValue(comp, fieldPath)),
    );

    return shouldIncludeSalesComparisonRow({
      field,
      orderFeatures,
      includeTotalCostToCure: includeTotalCostToCure ?? false,
      includeCostApproach,
      cellValues,
    });
  });

  if (!hasArvOption) {
    return (
      <>
        {pages.map((chunk, pageIndex) => (
          <SalesComparisonTablePages
            key={`sales-comparison-page-${pageIndex}`}
            subjectAddress={subjectAddress}
            subject={subjectSalesComparisonData as SalesComparisonGridColumnData}
            chunk={chunk}
            filteredRows={filteredRows}
            pageIndex={pageIndex}
            pageKeyPrefix="sales-comparison"
            opinionOfValue={opinionOfMarketValue}
          />
        ))}
      </>
    );
  }

  return (
    <>
      {aivPages.map((chunk, pageIndex) => (
        <SalesComparisonTablePages
          key={`sales-comparison-aiv-page-${pageIndex}`}
          subjectAddress={subjectAddress}
          subject={subjectSalesComparisonData as SalesComparisonGridColumnData}
          chunk={chunk}
          filteredRows={filteredRows}
          pageIndex={pageIndex}
          pageKeyPrefix="sales-comparison-aiv"
          compType="aiv"
          badge="aiv"
          opinionOfValue={opinionOfMarketValue}
        />
      ))}
      {arvPages.map((chunk, pageIndex) => (
        <SalesComparisonTablePages
          key={`sales-comparison-arv-page-${pageIndex}`}
          subjectAddress={subjectAddress}
          subject={subjectArvSalesComparisonData as SalesComparisonGridColumnData}
          chunk={chunk}
          filteredRows={filteredRows}
          pageIndex={pageIndex}
          pageKeyPrefix="sales-comparison-arv"
          compType="arv"
          badge="arv"
          opinionOfValue={opinionOfAfterRepairValue}
        />
      ))}
    </>
  );
};
